"use client";

import { useRef, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import LoadingBar, { LoadingBarRef } from "react-top-loading-bar";
import {
  NAVIGATION_START_EVENT,
  type NavigationStartDetail,
} from "@/lib/navigation-feedback";

export function TopLoadingBar() {
  const ref = useRef<LoadingBarRef>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentUrlRef = useRef("");

  useEffect(() => {
    currentUrlRef.current = `${pathname}?${searchParams.toString()}`;

    ref.current?.complete();
  }, [pathname, searchParams]);

  useEffect(() => {
    const toComparablePath = (url: string) => {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}?${parsed.searchParams.toString()}`;
    };

    // A same-URL push never changes pathname/searchParams, so complete()
    // would never fire and the bar would crawl forever — suppress it, same
    // as the anchor-click path below.
    const handleStart = (event: Event) => {
      const targetUrl = (event as CustomEvent<NavigationStartDetail>).detail
        ?.targetUrl;
      if (targetUrl && toComparablePath(targetUrl) === currentUrlRef.current) {
        return;
      }
      ref.current?.continuousStart();
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;

      if (
        anchor.target ||
        anchor.hasAttribute("download") ||
        anchor.getAttribute("rel") === "external"
      ) {
        return;
      }

      const nextUrl = new URL(anchor.href);
      if (nextUrl.origin !== window.location.origin) return;

      if (toComparablePath(anchor.href) === currentUrlRef.current) return;

      ref.current?.continuousStart();
    };

    window.addEventListener(NAVIGATION_START_EVENT, handleStart);
    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, handleStart);
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, []);

  return (
    <LoadingBar
      ref={ref}
      height={2}
      shadow={true}
      color="hsl(var(--brand-primary))"
    />
  );
}
